import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "./pos.css";
import {
  getAuthToken,
  getCurrentShopId,
} from "./auth";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const getToday = () => new Date().toISOString().slice(0, 10);

/* const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}; */

const parseItems = (sale) => {
  try {
    return typeof sale.items === "string"
      ? JSON.parse(sale.items || "[]")
      : sale.items || [];
  } catch {
    return [];
  }
};

const getItemCostPrice = (item) => {
  return Number(
    item.cost_price ||
      item.costPrice ||
      item.cost ||
      item.buying_price ||
      item.buyingPrice ||
      0
  );
};

const getSaleProfitTotals = (sale) => {
  const items = parseItems(sale);

  let salesTotal = 0;
  let costTotal = 0;
  let profitTotal = 0;
  let drsTotal = 0;

  for (const item of items) {
    const qty = Number(item.qty || item.quantity || 1);
    const price = Number(item.price || 0);
    const costPrice = getItemCostPrice(item);
    const drsAmount = Number(item.drs_amount || item.drsAmount || 0);

    const itemSales = price * qty;
    const itemCost = costPrice * qty;
    const itemProfit = itemSales - itemCost;

    salesTotal += itemSales;
    costTotal += itemCost;
    profitTotal += itemProfit;
    drsTotal += drsAmount * qty;
  }

  const margin = salesTotal > 0 ? (profitTotal / salesTotal) * 100 : 0;

  return {
    salesTotal,
    costTotal,
    profitTotal,
    margin,
    drsTotal,
  };
};

const buildCategoryProfit = (sales = []) => {
  const categoryMap = {};

  sales
    .filter((sale) => !sale.is_void)
    .forEach((sale) => {
      const items = parseItems(sale);

      items.forEach((item) => {
        const category = item.category || "uncategorized";
        const qty = Number(item.qty || item.quantity || 1);
        const price = Number(item.price || 0);
        const costPrice = getItemCostPrice(item);

        const itemSales = price * qty;
        const itemCost = costPrice * qty;
        const itemProfit = itemSales - itemCost;

        if (!categoryMap[category]) {
          categoryMap[category] = {
            category,
            qty: 0,
            salesTotal: 0,
            costTotal: 0,
            profitTotal: 0,
          };
        }

        categoryMap[category].qty += qty;
        categoryMap[category].salesTotal += itemSales;
        categoryMap[category].costTotal += itemCost;
        categoryMap[category].profitTotal += itemProfit;
      });
    });

  return Object.values(categoryMap)
    .map((cat) => ({
      ...cat,
      margin:
        cat.salesTotal > 0 ? (cat.profitTotal / cat.salesTotal) * 100 : 0,
    }))
    .sort((a, b) => b.profitTotal - a.profitTotal);
};

const calculateProfitSummary = (sales = []) => {
  return sales
    .filter((sale) => !sale.is_void)
    .reduce(
      (summary, sale) => {
        const totals = getSaleProfitTotals(sale);

        summary.totalTransactions += 1;
        summary.salesTotal += totals.salesTotal;
        summary.costTotal += totals.costTotal;
        summary.profitTotal += totals.profitTotal;
        summary.drsTotal += totals.drsTotal;

        return summary;
      },
      {
        totalTransactions: 0,
        salesTotal: 0,
        costTotal: 0,
        profitTotal: 0,
        drsTotal: 0,
      }
    );
};

const formatPdfMoney = (value) => {
  return `EUR ${Number(value || 0).toFixed(2)}`;
};

const formatPdfPercent = (value) => {
  return `${Number(value || 0).toFixed(1)}%`;
};

const formatPdfDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "-";
  }
};

const truncateText = (value, maxLength = 25) => {
  const text = String(value ?? "-");

  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3)}...`;
};


const ProfitReportPage = () => {
const shopId = getCurrentShopId();

  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    salesTotal: 0,
    costTotal: 0,
    profitTotal: 0,
    drsTotal: 0,
  });
  const [categoryProfit, setCategoryProfit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tills, setTills] = useState([]);
const [selectedTillId, setSelectedTillId] = useState("all");
const [tillsLoading, setTillsLoading] = useState(false);

const loadTills = async () => {
  try {
    setTillsLoading(true);

    const token = getAuthToken();

    const response = await axios.get(`${API_URL}/tills`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tillList = Array.isArray(response.data?.tills)
      ? response.data.tills
      : [];

    setTills(tillList);
  } catch (err) {
    console.error(
      "Load profit report tills error:",
      err.response?.data || err.message
    );

    setTills([]);
  } finally {
    setTillsLoading(false);
  }
};

  const loadProfitReport = async () => {
    try {
      setLoading(true);
      setError("");

const token = getAuthToken();

if (!token) {
  throw new Error(
    "Your login session has expired. Please log in again."
  );
}

const res = await axios.get(`${API_URL}/sales`, {
  params: {
    shop_id: shopId,
    from: fromDate,
    to: toDate,
    till_id:
      selectedTillId === "all"
        ? "all"
        : Number(selectedTillId),
  },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

      const loadedSales = res.data.sales || [];

      const profitSummary = calculateProfitSummary(loadedSales);
      const categories = buildCategoryProfit(loadedSales);

      setSales(loadedSales);
      setSummary(profitSummary);
      setCategoryProfit(categories);
    } catch (err) {
      console.error("Load profit report error:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to load profit report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadTills();
  loadProfitReport();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const profitMargin =
    summary.salesTotal > 0
      ? (summary.profitTotal / summary.salesTotal) * 100
      : 0;


      const getSelectedTillLabel = () => {
  if (selectedTillId === "all") {
    return "All Tills";
  }

  const selectedTill = tills.find(
    (till) =>
      String(till.id) === String(selectedTillId)
  );

  if (!selectedTill) {
    return "Selected Till";
  }

  return `${selectedTill.name} (${selectedTill.till_code})`;
};

      const createProfitReportPdf = (action = "download") => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 12;
  let y = 15;

  const addPageIfNeeded = (requiredSpace = 10) => {
    if (y + requiredSpace > pageHeight - 12) {
      doc.addPage();
      y = 15;
    }
  };

 /*  const drawText = (text, x, size = 10, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.text(String(text), x, y);
  }; */

  const drawLine = () => {
    doc.setDrawColor(180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  };

  const drawTableHeader = (columns, widths) => {
    addPageIfNeeded(12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    let x = margin;

    columns.forEach((column, index) => {
      doc.text(column, x, y);
      x += widths[index];
    });

    y += 4;
    doc.setDrawColor(190);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
  };

  const drawTableRow = (cells, widths) => {
    addPageIfNeeded(9);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    let x = margin;

    cells.forEach((cell, index) => {
      doc.text(String(cell), x, y);
      x += widths[index];
    });

    y += 6;
  };

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Profit Report", margin, y);

  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Shop ID: ${shopId || "-"}`, margin, y);
  doc.text(`Date: ${new Date().toLocaleString()}`, pageWidth - margin - 65, y);

  y += 6;

  doc.text(`From: ${fromDate}`, margin, y);
 doc.text(
  `Till: ${getSelectedTillLabel()}`,
  margin + 90,
  y
);

  y += 8;
  drawLine();

  // Summary
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Summary", margin, y);

  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  doc.text(`Total Transactions: ${summary.totalTransactions}`, margin, y);
  y += 6;

  doc.text(`Total Sales: ${formatPdfMoney(summary.salesTotal)}`, margin, y);
  doc.text(`Total Cost: ${formatPdfMoney(summary.costTotal)}`, margin + 70, y);
  y += 6;

  doc.text(`Gross Profit: ${formatPdfMoney(summary.profitTotal)}`, margin, y);
  doc.text(`Profit Margin: ${formatPdfPercent(profitMargin)}`, margin + 70, y);
  y += 6;

  doc.text(`DRS Total: ${formatPdfMoney(summary.drsTotal)}`, margin, y);

  y += 8;
  drawLine();

  // Category Profit
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Category Profit", margin, y);

  y += 8;

  const categoryColumns = ["Category", "Qty", "Sales", "Cost", "Profit", "Margin"];
  const categoryWidths = [45, 18, 32, 32, 32, 22];

  drawTableHeader(categoryColumns, categoryWidths);

  if (categoryProfit.length === 0) {
    drawTableRow(["No category profit found", "", "", "", "", ""], categoryWidths);
  } else {
    categoryProfit.forEach((cat) => {
      drawTableRow(
        [
          truncateText(cat.category, 22),
          Number(cat.qty || 0).toFixed(2),
          formatPdfMoney(cat.salesTotal),
          formatPdfMoney(cat.costTotal),
          formatPdfMoney(cat.profitTotal),
          formatPdfPercent(cat.margin),
        ],
        categoryWidths
      );
    });
  }

  y += 6;
  drawLine();

  // Sales Profit List
  addPageIfNeeded(20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Sales Profit List", margin, y);

  y += 8;

  const salesColumns = [
  "Sale",
  "Date",
  "Till",
  "Cashier",
  "Sales",
  "Cost",
  "Profit",
  "Margin",
];

const salesWidths = [
  17,
  38,
  27,
  27,
  23,
  23,
  23,
  18,
];

  drawTableHeader(salesColumns, salesWidths);

  if (sales.length === 0) {
    drawTableRow(
  ["No sales found", "", "", "", "", "", "", ""],
  salesWidths
);
  } else {
    sales.forEach((sale) => {
      const profit = getSaleProfitTotals(sale);

      drawTableRow(
       [
  `#${sale.id}`,
  truncateText(
    formatPdfDate(sale.created_at),
    18
  ),
  truncateText(
    sale.till_name || "Unknown",
    13
  ),
  truncateText(
    sale.user_name || "Unknown",
    13
  ),
  formatPdfMoney(profit.salesTotal),
  formatPdfMoney(profit.costTotal),
  formatPdfMoney(profit.profitTotal),
  formatPdfPercent(profit.margin),
],
        salesWidths
      );
    });
  }

  // Footer page numbers
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin - 25,
      pageHeight - 8
    );
  }

  const tillFileLabel =
  selectedTillId === "all"
    ? "all-tills"
    : `till-${selectedTillId}`;

const fileName =
  `profit-report-shop-${shopId}-${tillFileLabel}-${fromDate}-to-${toDate}.pdf`;

  if (action === "print") {
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl);

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      doc.save(fileName);
    }

    return;
  }

  doc.save(fileName);
};

  return (
    <div className="sales-page">
      <div className="sales-header">
        <div>
          <h1 style={{ color: "#FFF" }}>Profit Report</h1>
          <p>View sales, cost, gross profit and profit margin.</p>
        </div>

        <div className="sales-badge">
  {getSelectedTillLabel()}
</div>
      </div>

      <div className="sales-filter-card">
        <div className="sales-filter">
          <label>
            From Date
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label>
            To Date
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

        <label>
  Till

  <select
    value={selectedTillId}
    onChange={(e) => {
      setSelectedTillId(e.target.value);

      setSales([]);
      setCategoryProfit([]);

      setSummary({
        totalTransactions: 0,
        salesTotal: 0,
        costTotal: 0,
        profitTotal: 0,
        drsTotal: 0,
      });
    }}
    disabled={tillsLoading}
    className="sales-till-select"
  >
    <option value="all">All Tills</option>

    {tills.map((till) => (
      <option
        key={till.id}
        value={String(till.id)}
      >
        {till.name} ({till.till_code})
      </option>
    ))}
  </select>
</label>

          <button onClick={loadProfitReport} disabled={loading}>
            {loading ? "Loading..." : "Load Profit Report"}
          </button>

        <button
  type="button"
  onClick={() => createProfitReportPdf("download")}
  disabled={loading}
>
  Export PDF
</button>

<button
  type="button"
  onClick={() => createProfitReportPdf("print")}
  disabled={loading}
>
  Print PDF
</button>

        </div>
      </div>

      {error && <div className="sales-error">{error}</div>}

      <div className="sales-summary-grid">
        <div className="sales-summary-card">
          <span>Total Transactions</span>
          <strong>{summary.totalTransactions}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Total Sales</span>
          <strong>€{Number(summary.salesTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Total Cost</span>
          <strong>€{Number(summary.costTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Gross Profit</span>
          <strong>€{Number(summary.profitTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Profit Margin</span>
          <strong>{Number(profitMargin || 0).toFixed(1)}%</strong>
        </div>

        <div className="sales-summary-card">
          <span>DRS Total</span>
          <strong>€{Number(summary.drsTotal || 0).toFixed(2)}</strong>
        </div>
      </div>

      <div className="sales-table-card">
        <h2>Category Profit</h2>

        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Qty</th>
                <th>Sales</th>
                <th>Cost</th>
                <th>Profit</th>
                <th>Margin</th>
              </tr>
            </thead>

            <tbody>
              {categoryProfit.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-sales">
                    No category profit found.
                  </td>
                </tr>
              ) : (
                categoryProfit.map((cat) => (
                  <tr key={cat.category}>
                    <td>{cat.category}</td>
                    <td>{Number(cat.qty || 0).toFixed(2)}</td>
                    <td>€{Number(cat.salesTotal || 0).toFixed(2)}</td>
                    <td>€{Number(cat.costTotal || 0).toFixed(2)}</td>
                    <td>€{Number(cat.profitTotal || 0).toFixed(2)}</td>
                    <td>{Number(cat.margin || 0).toFixed(1)}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sales-table-card">
        <h2>Sales Profit List</h2>

        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>
                
  <th>Sale ID</th>
  <th>Date</th>
  <th>Till</th>
  <th>Cashier</th>
  <th>Total</th>
  <th>Cost</th>
  <th>Profit</th>
  <th>Margin</th>
</tr>
             
            </thead>

            <tbody>
              {sales.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-sales">
                    No sales found for selected dates.
                  </td>
                </tr>
              ) : (
                sales.map((sale) => {
                  const profit = getSaleProfitTotals(sale);

                  return (
                    <tr key={sale.id}>
                      <td>#{sale.id}</td>

                      <td>
                        {sale.created_at
                          ? new Date(sale.created_at).toLocaleString()
                          : "-"}
                      </td>

                          <td>
  <strong>
    {sale.till_name || "Legacy / Unknown"}
  </strong>

  {sale.till_code && (
    <div className="sales-cell-subtext">
      {sale.till_code}
    </div>
  )}
</td>

<td>
  <strong>
    {sale.user_name || "Unknown"}
  </strong>

  {sale.user_email && (
    <div className="sales-cell-subtext">
      {sale.user_email}
    </div>
  )}
</td>

                      <td>€{Number(profit.salesTotal || 0).toFixed(2)}</td>
                      <td>€{Number(profit.costTotal || 0).toFixed(2)}</td>
                      <td>€{Number(profit.profitTotal || 0).toFixed(2)}</td>
                      <td>{Number(profit.margin || 0).toFixed(1)}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfitReportPage;